/**
 * OrgCrest — a company's mark, derived from its orgId.
 *
 * An org has no logo and never will have one at signup time, but the moment
 * the product says "Tejjogani Diamonds is already on DiaEx" it needs to show
 * something that reads as *that company* and not as a generic building icon.
 * So: a deterministic facet drawing, seeded from the orgId, stable for the
 * life of the org and identical on every device.
 *
 * ── WHY INLINE SVG AND NOT `DiamondHero` ──────────────────────────────────
 * `components/three/DiamondHero.tsx` is a real refractive brilliant and it is
 * tempting here. It is also WebGL2, ~a megabyte of three.js, and desktop-only.
 * This renders at 28px inside a header, inside a signup funnel, on a low-end
 * Android — the one place in the product where a dropped frame costs a
 * conversion. Inline SVG, no dependencies, no raster, no network.
 *
 * ── THE ANTI-NFT RULES (the whole defence against avatar drift) ───────────
 * Three derived indices and nothing else:
 *     archetype   h % 3          brilliant | step-cut | marquise
 *     facets      (h >>> 3) % 4
 *     accent      (h >>> 6) % 8  an index into ONE curated ramp
 * The ramp is navy + champagne + a single cool blue — the app's own palette.
 * There is no hue rotation, no free colour, no randomness outside those three
 * numbers. Add a fourth parameter and this stops being a crest and starts
 * being a generated avatar; `OrgCrest.test.tsx` asserts the bound.
 */

/** FNV-1a, 32-bit. Small, dependency-free, and well spread over short ASCII
 *  ids — which Firestore auto-ids are. Not a security primitive and not used
 *  as one. */
export function fnv1a(input: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    // ×16777619 without overflowing into float territory.
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/** navy-900 · navy-700 · navy-500(ish) · info · navy-300 · gold-600 · gold-500 ·
 *  gold-300. Every one of these is already a token in `styles/index.css`. */
export const CREST_RAMP = [
  '#0a1f3c', '#0e2a4f', '#23456f', '#2f5d8a',
  '#6b83a3', '#a8842a', '#c9a94a', '#e4cd86',
] as const

export type CrestArchetype = 'brilliant' | 'step' | 'marquise'

export interface CrestSeed {
  archetype: CrestArchetype
  /** 0–3. Which interior facet pattern the archetype draws. */
  facets: number
  /** The accent colour, taken from CREST_RAMP and nowhere else. */
  accent: string
}

const ARCHETYPES: readonly CrestArchetype[] = ['brilliant', 'step', 'marquise']

export function crestSeed(orgId: string): CrestSeed {
  const h = fnv1a(orgId || 'diaex')
  return {
    archetype: ARCHETYPES[h % 3],
    facets: (h >>> 3) % 4,
    accent: CREST_RAMP[(h >>> 6) % 8],
  }
}

/** The interior facet lines per archetype × variant. Hand-drawn on a 48-unit
 *  box so every path is legible at 28px; `vector-effect` keeps the stroke
 *  1px-ish at every size instead of thickening with the scale. */
function outlineFor(a: CrestArchetype): string {
  switch (a) {
    // Emerald cut — an octagon, not a rectangle. A plain box reads as an icon
    // of a box; the clipped corners are what make it a stone.
    case 'step':     return 'M17 6h14l7 7v22l-7 7H17l-7-7V13l7-7z'
    case 'marquise': return 'M24 4c8 7 12 13 12 20s-4 13-12 20c-8-7-12-13-12-20s4-13 12-20z'
    default:         return 'M13 9h22l9 11-20 19L4 20l9-11z'
  }
}

function facetsFor(a: CrestArchetype, v: number): string[] {
  if (a === 'step') {
    const inner = 'M19 11h10l5 5v16l-5 5H19l-5-5V16l5-5z'
    return [
      [inner, 'M23 16h2l3 3v10l-3 3h-2l-3-3V19l3-3z'],
      [inner, 'M14 19h20M14 29h20'],
      [inner, 'M17 6l2 5M31 6l-2 5M17 42l2-5M31 42l-2-5'],
      [inner, 'M14 24h20'],
    ][v]
  }
  if (a === 'marquise') {
    return [
      ['M24 4v40', 'M12 24h24'],
      ['M24 4v40', 'M15 15c6 4 12 4 18 0M15 33c6-4 12-4 18 0'],
      ['M24 4v40', 'M12 24h24', 'M17 12l14 24M31 12L17 36'],
      ['M24 4v40'],
    ][v]
  }
  return [
    ['M4 20h40', 'M13 9l6 11 5 19 5-19 6-11'],
    ['M4 20h40', 'M19 20l5 19 5-19', 'M13 9l6 11M35 9l-6 11'],
    ['M4 20h40', 'M13 9l6 11 5 19 5-19 6-11', 'M19 20h10'],
    ['M4 20h40', 'M24 20v19'],
  ][v]
}

/**
 * @param orgId  the seed. Stable for the life of the org.
 * @param name   when present the crest is announced as "{name} crest";
 *               without it the crest is decorative and hidden from AT.
 */
export function OrgCrest({
  orgId,
  size = 48,
  name,
  className,
  ...rest
}: {
  orgId: string
  /** 28 (header slot) · 48 (card) · 96 (org profile hero). */
  size?: 28 | 48 | 96
  name?: string | null
  className?: string
} & Omit<React.SVGProps<SVGSVGElement>, 'name'>) {
  const { archetype, facets, accent } = crestSeed(orgId)
  const label = name?.trim()
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      fill="none"
      strokeWidth={1.6}
      strokeLinejoin="round"
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
      className={className}
      {...(label ? { role: 'img', 'aria-label': `${label} crest` } : { 'aria-hidden': true })}
      {...rest}
    >
      {/* The body is always paper — a crest is a mark on a surface, not a
          coloured blob. Only the rim and ONE facet carry the seeded accent. */}
      <path d={outlineFor(archetype)} fill="#ffffff" stroke={accent} />
      {facetsFor(archetype, facets).map((d, i) => (
        <path
          key={d}
          d={d}
          stroke={i === 0 ? accent : '#23456f'}
          opacity={i === 0 ? 1 : 0.45}
        />
      ))}
    </svg>
  )
}
